import React from "react";
import styles from "./MyLearningCard.module.scss";
import { Box, Chip, Typography, useMediaQuery } from "@mui/material";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { useTranslation } from "react-i18next";
import NoImageThumbnail from "../../assets/icons/learning_no_image.svg";
import EducationContentIcons from "../../components/v2/education-content-icons/EducationContentIcons";
import AudioIcon from "../../components/icon-components/AudioIcon";
import CheckedCircleIcon from "../../components/icon-components/CheckedCircleIcon";

interface MyLearningCardProps {
  imageSrc: string;
  imageAlt?: string;
  isNew?: boolean;
  duration: number;
  title: string;
  height?: string;
  width?: string;
  contentType: string;
  activityPoints: number;
  changeHorizontalLayout?: boolean;
  showButton?: boolean;
  activityCompletionStatus: string;
  hasAudioLink?: boolean;
  isCompleted?: boolean;
  onButtonClick?: () => void;
}

const MyLearningCard: React.FC<MyLearningCardProps> = ({
  imageSrc,
  imageAlt = "Thumbnail",
  isNew = false,
  duration,
  title,
  height,
  width,
  contentType,
  activityPoints,
  onButtonClick,
  changeHorizontalLayout = false,
  hasAudioLink = false,
  showButton = true,
  isCompleted,
  activityCompletionStatus,
}) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  const shouldUseHorizontalLayout = isSmallScreen && changeHorizontalLayout;

  const renderVerticalLayout = () => (
    <Box
      className={styles.cardContainer}
      maxWidth={width}
      height={height}
      onClick={!showButton ? onButtonClick : undefined}
      sx={{ cursor: !showButton ? "pointer" : "default" }}
    >
      <Box
        className={styles.imageSection}
        sx={{
          height: !showButton ? "60%" : "50%",
          maxHeight: !showButton && imageSrc ? "180px" : "100%",
        }}
      >
        {isNew && (
          <Chip
            label={t("educationContent.new")}
            className={styles.newChip}
          />
        )}
        <img
          src={imageSrc || NoImageThumbnail}
          alt={imageAlt}
          className={imageSrc ? styles.thumbnailImage : styles.noThumbnailImage}
        />

        {isCompleted ? (
          <Box className={styles.completedBox}>
            <CheckedCircleIcon
              width={20}
              height={20}
              variant={"completed"}
            />
          </Box>
        ) : (
          activityPoints !== 0 && (
            <Box className={styles.activityPointsBox}>
              <Typography variant="body2" fontWeight={"bold"}>
                + {activityPoints}
              </Typography>
            </Box>
          )
        )}
      </Box>

      <Box className={styles.horizontalCardContent}>
        <Box pt={2}>
        <Box
          display="flex"
          alignItems="center"
          className={styles.videoInfo}
        >
          <EducationContentIcons
            type={contentType}
            height={16}
            width={17}
            color="#4E7E6E"
          />
          <Typography
            variant="body2"
            className={styles.videoText}
          >
            {contentType === "videos" && (
                <>
                  {t("video")}
                  {duration > 0 &&
                    ` | ${
                      duration! < 1
                        ? t("educationContent.lessThanAMin")
                        : `${duration} ${t("educationContent.min")}`
                    }`}
                </>
              )}
            {contentType === "articles" && t("article")}
            {contentType === "URL" && t("URL")}
          </Typography>

          {contentType === "articles" && hasAudioLink && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Typography
                variant="body2"
                className={styles.videoText}
              >
                |
              </Typography>
              <AudioIcon
                style={{ fontSize: "16px", verticalAlign: "middle" }}
              />
              <Typography
                variant="body2"
                className={styles.videoText}
              >
                {t("educationContent.audio")}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ height: "80%", paddingTop: showButton ? 0 : 1 }}>
          <Typography
            variant="body1"
            className={styles.titleText}
          >
            {title}
          </Typography>
          {showButton && (
            <Box
              pt={1}
            >
              <CustomButton
                variant="outlined"
                text={
                  {
                    videos: t("carePlan.watch"),
                    articles: t("carePlan.read"),
                    URL: t("educationContent.open"),
                  }[contentType] || t("educationContent.open")
                }
                onClick={onButtonClick}
              />
            </Box>
          )}
        </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderHorizontalLayout = () => (
    <Box
      className={styles.cardHorizontal}
      onClick={!showButton ? onButtonClick : undefined}
      sx={{ cursor: !showButton ? 'pointer' : 'default' }}
    >
      <Box
        className={styles.cardImage}
        style={{
          backgroundImage: imageSrc ? `url(${imageSrc})` : "none",
          backgroundColor: !imageSrc ? "#f2f0ec" : "#fff",
        }}
      >
        {isNew && (
          <Chip
            label={t("educationContent.new")}
            className={styles.newChip}
          />
        )}

        {!imageSrc && (
          <img
            src={NoImageThumbnail}
            alt={title}
            style={{ width: 40, height: 40 }}
          />
        )}
      </Box>

      <Box className={styles.cardContent}>
        <Typography
          variant="body2"
          gutterBottom
          maxWidth={"85%"}
        >
          {title}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          gap={.5}
          mt={1}
        >
          <EducationContentIcons
            type={contentType}
            height={16}
            width={17}
            color="#4E7E6E"
          />
          {contentType === "videos" && (
            <>
              <Typography variant="body2" color="textSecondary">
                {t("video")}
              </Typography>
              {duration > 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {duration < 1
                    ? `|  ${t("educationContent.lessThanAMin")}`
                    : `|  ${duration} ${t("educationContent.min")}`}
                </Typography>
              )}
            </>
          )}
          {contentType === "articles" && (
            <Typography variant="body2" color="textSecondary">
              {t("article")}
            </Typography>
          )}
          {contentType === "URL" && (
            <Typography variant="body2" color="textSecondary">
              {t("URL")}
            </Typography>
          )}
          {contentType === "articles" && hasAudioLink && (
            <Box
              display="flex"
              alignItems="center"
              gap={.5}
            >
              <Typography
                variant="body2"
                className={styles.videoText}
              >
                |{" "}
              </Typography>
              <AudioIcon
                style={{ fontSize: "16px", verticalAlign: "middle" }}
              />
              <Typography
                variant="body2"
                className={styles.videoText}
              >
                {t("educationContent.audio")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {isCompleted ? (
        <Box className={styles.completedBox}>
          <CheckedCircleIcon
            width={20}
            height={20}
            variant={"completed"}
          />
        </Box>
      ) : (
        activityPoints !== 0 && (
          <Box className={styles.activityPointsBoxSmall}>
            <Typography variant="bodySmall" fontWeight={"bold"} color="black">
              + {activityPoints}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );

  return shouldUseHorizontalLayout
    ? renderHorizontalLayout()
    : renderVerticalLayout();
};

export default MyLearningCard;
